// @ts-nocheck
import {Button, Text, Title, Flex, Box, Anchor, LoadingOverlay, Tooltip, ActionIcon, Checkbox, Badge, Menu} from '@mantine/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from '@mantine/form';
import Router, {useRouter} from 'next/router';
import {showNotification, updateNotification} from '@mantine/notifications';
import * as Enums from "@/utils/enums";
import Fetch from '@/utils/Fetch';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import moment from 'moment';
import Helper from "@/utils/helper";
import {previewFormDefinition} from "@/utils/form-definition-preview";
import Link from "next/link";
import ScTextControl from "@/components/sc-controls/form-controls/v2/sc-text-control";
import ScTextAreaControl from "@/components/sc-controls/form-controls/v2/ScTextAreaControl";
import SCDropdownList from "@/components/sc-controls/form-controls/sc-dropdownlist";
import SCMultiSelect from "@/components/sc-controls/form-controls/sc-multiselect";
import SCSwitch from "@/components/sc-controls/form-controls/sc-switch";
import MinuteSelector from "@/components/selectors/minute-selector";
import SectionTable from "@/PageComponents/SectionTable/SectionTable";
import FormDefinitionVersionHistory from '@/PageComponents/Settings/FormDefinitionVersionHistory';
import PageTabs from '@/PageComponents/Layout/PageTabs';
import ManageFormDefinitionField from '@/components/modals/form-definition/manage-form-definition-field';
import {IconEdit, IconPlus, IconInfoCircle, IconEye, IconArchive, IconArchiveOff, IconDotsVertical, IconArrowBackUp} from "@tabler/icons-react";
import {IconTrash} from "@tabler/icons";
import FormDefinitionFields from "@/components/form-definition/items";
import ConfirmAction from "@/components/modals/confirm-action";
import ManageSection from "@/components/modals/section/manage-section";

// Supporting Types for nested objects

interface JobType {
    ID: string;
    Name: string;
}

interface Section {
    ID: string; // Section ID
    ItemID: string; // Unique identifier for the item
    DisplayOrder: number; // Order of display within the form
    Heading: string; // Heading of the section
    Description?: string; // Optional description
    IsActive: boolean; // Indicates if the section is active
    ParentSectionID?: string | null; // Parent section ID, if applicable
    Repeatable: boolean; // Indicates if the section is repeatable
    Module: number; // Module ID associated with the section
    CreatedBy: string; // User who created the section
    CreatedDate: string; // Creation timestamp
    ModifiedBy: string; // User who last modified the section
    ModifiedDate: string; // Last modification timestamp
    RowVersion: string; // Concurrency tracking row version
}

interface FormDefinitionField {
    ID: string; // Unique identifier for the field
    MasterID: string; // Master identifier
    Description: string; // Description of the field
    DataType: string; // Data type of the field (e.g., Text, Table, etc.)
    DisplayOrder: number; // The display order of the field
    Required: boolean; // Indicates if the field is required
    SectionID?: string | null; // ID of the section this field belongs to
    DataOption?: string; // Data option configuration (if applicable)
    Section?: Section; // Optional reference to the section
    // this is added for compatibility with sectionTable
    SectionHeading: string,
    LineNumber: number, // used by sectionTable
}

interface FormDefinition {
    ID?: string; // Unique identifier of the form (optional for new form)
    Name: string; // Name of the form
    Description?: string; // Description of the form
    Module?: string | null; // Selected module (optional, can be null)
    FormRule?: number; // Rule that determines form classification
    ExpireTimespan?: number; // Time span (in minutes) until the form expires
    NonExpiring?: boolean; // Indicates if the form does not expire
    StructureLocked?: boolean; // Determines if the structure is locked and uneditable
    IsActive: boolean; // Active status of the form
    JobTypes: Array<JobType>; // Job types linked to the form
    Sections: Array<Section>; // Sections within the form
    FormDefinitionFields?: Array<FormDefinitionField>; // Fields that make up the form
    Version: number; // Version number of the form
    CreatedDate?: string; // Creation date
    CreatedBy?: string; // Created by user information
    ModifiedDate?: string; // Last modified date
    ModifiedBy?: string; // Modified by user information
    FormDefinitionStatus?: number; // Status of the form (Published, Draft, etc.)
    MasterID: string; // Master ID for linking related records
    RowVersion?: string; // Row version for concurrency tracking
    MultiUse?: boolean; // Indicates if the form can be reused (on mobile)
}

// Define types
interface ManageFormDefinitionProps {
    formDefinition: FormDefinition; // Replace `any` with a more specific type if available
    // setFormDefinition: (data: any) => void; // Replace `any` with specific type
    isNew: boolean;
    accessStatus: any;
}


// Enforce sensible caps to avoid rendering/printing issues
const DESCRIPTION_MAX = 400;
const NAME_MAX = 125;

const reorderSectionsToBottom: (input: FormDefinitionField[]) => FormDefinitionField[] = (input: any) => {
    const reorganized = input.sort((a: any, b: any) => {
        if (!a.SectionID && b.SectionID) {
            return -1; // Place items without SectionID at the top
        } else if (a.SectionID && !b.SectionID) {
            return 1; // Place items with SectionID at the bottom
        } else {
            // Both items either have or don't have SectionID, sort by DisplayOrder
            return a.LineNumber - b.LineNumber;
        }
    }).map((x: any, i: any) => ({...x, DisplayOrder: i, LineNumber: i + 1}));
    return reorganized
}

// Every form must be linked to a module - forms saved without one (FormRule None) are treated as Job
const formRuleOption = (formRule?: number) =>
    Enums.getEnumItemsVD(Enums.FormRule).find((x: any) => x.value === (formRule || Enums.FormRule.Job));

// Convert React component to TypeScript
const ManageFormDefinition: React.FC<ManageFormDefinitionProps> = ({ formDefinition: initialFormDefinition, isNew, accessStatus }: any) => {

    const [formDefinition, setFormDefinition] = useState(initialFormDefinition);
    const queryClient = useQueryClient();

    // Structure-locked forms (e.g. integration-managed ones) cannot be renamed, deactivated or restructured.
    const structureLocked = !isNew && !!formDefinition?.StructureLocked;

    const formDefinitionQuery = useQuery(['refetchFormDefinition', formDefinition.ID], () => Fetch.get({ url: `/FormDefinition/${formDefinition.ID}` }), {
        refetchOnWindowFocus: true,
        // refetchOnReconnect: true,
        refetchOnMount: false,
        enabled: !!formDefinition.ID && !isNew,
        onError: (error: any) => {
            console.log(error);
        }
    })

    // Update form definition when query data changes
    useEffect(() => {
        if (formDefinitionQuery.data) {
            setFormDefinition(formDefinitionQuery.data);
        }
    }, [formDefinitionQuery.data])

    // Querying Job Types

    const router = useRouter();

    const {mutate: mutateFormDefinition, isLoading: submitting} = useMutation(async (params: any) => {
        const requestConfig = isNew
            ? {
                method: 'post',
                url: '/FormDefinition',
            }
            : {
                method: 'put',
                url: `/FormDefinition`,
            };

        const res = await Fetch[requestConfig.method]({
            url: requestConfig.url,
            params
        })

        if(res.ID) {
            return res
        } else {
            throw new Error(res.serverMessage || res.message || 'something went wrong')
        }
    }, {
        onSuccess: async (result: any) => {
            if (result.ID) {
                const fieldsWithSectionData = result.FormDefinitionFields?.map((x: any) => ({'SectionHeading': x?.SectionHeading ?? (x.Section?.Heading || null), LineNumber: x.DisplayOrder + 1, ...x}))
                setFormDefinitionFields(reorderSectionsToBottom(fieldsWithSectionData))
                setFormDefinition(result);
                showNotification({
                    message: 'Form saved successfully',
                    color: 'scBlue',
                    autoClose: 1000
                });
                form.setValues({
                    ...result,
                    FormRule: formRuleOption(result.FormRule)
                })
                form.resetDirty()
                form.resetTouched()
                if (isNew || formDefinition.ID !== result.ID) {
                    const restoreRef = allowNavRef.current;
                    allowNavigation();
                    await Helper.waitABit()
                    await router.replace(`/settings/form/${result.ID}`, undefined, { shallow: !isNew });
                    await Helper.waitABit()
                    allowNavRef.current = restoreRef;
                }
                // Publishing promotes this same row to Published in place (no new row) - Version History's
                // Status/Current columns for it are now stale wherever that query is cached, including while
                // its tab sits unmounted (Tabs.Panel keepMounted={false}) rather than actively refetching.
                if (result.FormDefinitionStatus === Enums.FormDefinitionStatus.Published) {
                    queryClient.invalidateQueries({queryKey: ['formDefinitionVersions', result.MasterID]});
                }
            }
            setConfirming(false);
        },
        onError: (error: Error, variables: any) => {
            showNotification({
                message: error.message || 'Failed to save form definition',
                color: 'yellow.7',
            });
            setConfirming(false);
            // formDefinitionQuery.refetch();
        }
    });

    // Initialize Mantine Form
    const form = useForm({
        initialValues: {
            Name: isNew ? '' : formDefinition.Name,
            Description: isNew ? '' : formDefinition.Description,
            FormRule: formRuleOption(isNew ? Enums.FormRule.Job : formDefinition.FormRule),
            JobTypes: isNew ? [] : formDefinition.JobTypes,
            formExpires: isNew ? false : (!!formDefinition.ExpireTimespan && (formDefinition.ExpireTimespan > 0)),
            FormExpiresTimespan: isNew ? 0 : formDefinition.ExpireTimespan,
            IsActive: isNew ? true : formDefinition.IsActive,
            // Default to multi-use on create. For legacy records, fallback: MultiUse = !SingleUse
            MultiUse: isNew ? true : (formDefinition.MultiUse ?? !((formDefinition as any).SingleUse ?? false)),
            // Add other form fields as necessary
        },
        validate: {
            Name: (value: any) => {
                if ((value?.length ?? 0) < 1) return 'Name is required';
                return (value?.length ?? 0) > NAME_MAX ? `Name must be ${NAME_MAX} characters or fewer` : null;
            },
            Description: (value: any) => {
                const len = (value?.length ?? 0);
                return len > DESCRIPTION_MAX ? `  ` : null;
            },
            FormRule: (value: any) => (!value?.value ? 'Link form to is required' : null),
            // Add other validation rules as necessary
        },
        // Surface validation feedback before submission so users do not lose content
        validateInputOnBlur: true,
        validateInputOnChange: true,
        transformValues: (values: any) => ({
            ...values,
            module: isNew ? null : formDefinition.Module,
            FormRule: values.FormRule?.value,
            // formDefinitionStatus: structureChanged ? Enums.FormDefinitionStatus.Draft : formDefinition.FormDefinitionStatus
            // form.values.formDefinitionStatus || Enums.FormDefinitionStatus.Draft, // Ensure status
        })
    });

    // If an existing record has an overlong Description or Name, surface the error immediately on mount
    useEffect(() => {
        const nameLen = form.values?.Name?.length ?? 0;
        if (nameLen > NAME_MAX) {
            form.setFieldError('Name', `Name must be ${NAME_MAX} characters or fewer`);
        }
        const len = form.values?.Description?.length ?? 0;
        if (len > DESCRIPTION_MAX) {
            form.setFieldError('Description', ` `);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    // const [nextQueuedPayload, setNextQueuedPayload] = useState<any>(null)

    const submit = (values: typeof form.values, formEvent?: any, updatedFormDefinitionFields?: any[], status = formDefinition.FormDefinitionStatus) => {
        // Form Validation
        // const { isValid, errors } = form.validate();

        const formDefFieldsUnorganizedSections: FormDefinitionField[] = updatedFormDefinitionFields || formDefinitionFields

        const formDefFields: FormDefinitionField[] = reorderSectionsToBottom(formDefFieldsUnorganizedSections)

        setFormDefinitionFields(formDefFields);

        if (!form.isValid()) {
            form.validate()
            showNotification({
                title: 'Validation Error',
                message: 'There are errors in the form. Please fix them before submitting.',
                color: 'yellow.7',
            });
            return;
        }

        // Ensure FormDefinitionFields exist
        /*if (!isNew && formDefFields.length === 0) {
            showNotification({
                title: 'Missing Fields',
                message: 'Please add at least one field to the form definition.',
                color: 'yellow',
            });
            return;
        }*/

        if (!isNew && formDefFields.some((field: any) => !field.Description.trim())) {
            showNotification({
                title: 'Field Error',
                message: 'All form fields must have a description.',
                color: 'yellow',
            });
            return;
        }

        // Ensure expireTimespan is properly set
        const expireTimespan = values.formExpires ? values.FormExpiresTimespan : 0;

        /**
         * Function to map nested DataOption structures
         */
        const mapDataOptionTableToFormDefinitionFields = (dataOption: string) => {
            const tableStructure = JSON.parse(dataOption);
            return tableStructure.ColumnDefinitions.map((col: any) => ({
                ID: col.Name,
                MasterID: col.Name,
                Description: col.Label,
                DataType: col.DataType,
                DisplayOrder: 0,
                Required: col.Required,
                SectionID: null,
                DataOption: col.DataOption,
            }));
        };

        /**
         * Recursive function to process data options for fields like Tables, MultiSelect, etc.
         */
        const getDataOptions = (items: Array<any>) => {
            const options: Array<any> = [];

            items.forEach((item: any) => {
                if (Enums.FormDefinitionFieldTypes[item.DataType] === Enums.FormDefinitionFieldTypes.Table) {
                    const tableStructure = mapDataOptionTableToFormDefinitionFields(item.DataOption);
                    options.push(...getDataOptions(tableStructure));
                } else if (
                    [Enums.FormDefinitionFieldTypes.MultiSelect, Enums.FormDefinitionFieldTypes.Select].includes(
                        Enums.FormDefinitionFieldTypes[item.DataType]
                    )
                ) {
                    options.push({
                        FormDefinitionMasterID: formDefinition.MasterID,
                        ItemMasterID: item.MasterID,
                        DataOption: item.DataOption,
                    });
                }
            });

            return options;
        };

        // Gather data options
        const dataOptions = getDataOptions(formDefFields);

        const checkForItemStructureChanges = (items: any, originalItems = formDefinition.FormDefinitionFields || []) => {
            if (!items || !Array.isArray(items) || items.length !== originalItems.length) {
                return true; // Invalid or length mismatch
            }

            return items.some((item: any) => {
                const match = originalItems.find((x: any) => x.ID === item.ID);

                if (!match) return true; // No matching item found

                // Check table data recursively if DataType is 'Table'
                if (
                    Enums.FormDefinitionFieldTypes[match.DataType] === Enums.FormDefinitionFieldTypes.Table &&
                    Enums.FormDefinitionFieldTypes[item.DataType] === Enums.FormDefinitionFieldTypes.Table
                ) {
                    const tableStructure = mapDataOptionTableToFormDefinitionFields(item.DataOption);
                    const originalTableStructure = mapDataOptionTableToFormDefinitionFields(match.DataOption as string);

                    return checkForItemStructureChanges(tableStructure, originalTableStructure);
                }

                // Compare item properties
                return (
                    item.Description !== match.Description ||
                    item.DataType !== match.DataType ||
                    item.DisplayOrder !== match.DisplayOrder ||
                    item.Required !== match.Required ||
                    item.SectionID !== match.SectionID ||
                    item.Section?.DisplayOrder !== match.Section?.DisplayOrder
                );
            });
        };
        const structureChanged = checkForItemStructureChanges(formDefFields);

        const newStatus = formDefinition.StructureLocked ? Enums.FormDefinitionStatus.Published : structureChanged ? Enums.FormDefinitionStatus.Draft : status
        setConfirming(newStatus === Enums.FormDefinitionStatus.Published);

        const sections = formDefinition.Sections?.sort(
                (a: any, b: any) =>
                    (formDefFields.find(
                        (x: any) => x.SectionID === a.ID)?.DisplayOrder || 0) - (formDefFields.find((x: any) => x.SectionID === b.ID)?.DisplayOrder || 0)
            )
            .map((x: any, i: any) => ({...x, DisplayOrder: i})) ?? [];
        /*const sections = formDefFields.filter(x => !!x.SectionID)
            .map((x, i) => ({...x.Section, DisplayOrder: i}));*/


        // Sanitize/limit long text fields before submission
        const sanitizedValues = {
            ...values,
            Name: structureLocked ? formDefinition.Name : (values?.Name || '').slice(0, NAME_MAX),
            Description: (values?.Description || '').slice(0, DESCRIPTION_MAX),
        };

        // Prepare the Form Definition object for submission
        const formDefinitionToSave: any = {
            ...formDefinition,
            // ID: formDefinition.ID,
            ...sanitizedValues,
            ExpireTimespan: expireTimespan,
            FormDefinitionFields: formDefFields.map((x: any) => x.SectionID ?  ({...x, Section: sections.find((y: any) => y.ID === x.SectionID)}) : x), // map in new sections with new displayOrders
            FormDefinitionStatus: newStatus,
            Sections: sections, // updated sections with correct display orders
            // Sections: formDefFields.filter(x => !!x.Section && x.SectionID).map(x => x.Section),
            // RowVersion: formDefinition.RowVersion,
            // FormDefinitionStatus: form.values.formDefinitionStatus || Enums.FormDefinitionStatus.Draft, // Ensure status

        };

        const payload = {
            FormDefinition: formDefinitionToSave,
            FormDefinitionFields: formDefFields,
            JobTypes: sanitizedValues.JobTypes,
            FormDataOptions: dataOptions,
        } as any

        mutateFormDefinition(payload);

    };


    const {data: jobTypes, isLoading: jobTypesLoading, isError: jobTypesError} = useQuery(['jobTypes'],
        () => Fetch.get({
            url: "/JobType"
        }).then((jobTypeResponse: any) => {
            return jobTypeResponse.Results.sort((a: any, b: any) => {
                return a.Name > b.Name ? 1 : -1;
            });
        })
    )

    const [confirming, setConfirming] = useState(false)
    const [activeTab, setActiveTab] = useState<string | null>('Details');
    const pageTabs = useMemo(() => [
        { text: 'Details', suppressCount: true },
        { text: 'Version history', suppressCount: true },
    ], []);
    const isPublished = useMemo(() => formDefinition.FormDefinitionStatus === Enums.FormDefinitionStatus.Published, [formDefinition.FormDefinitionStatus]);
    const isDiscarded = useMemo(() => formDefinition.FormDefinitionStatus === Enums.FormDefinitionStatus.Discarded, [formDefinition.FormDefinitionStatus]);

    const [formDefinitionFields, setFormDefinitionFields] = useState<FormDefinitionField[]>(
        formDefinition.FormDefinitionFields
        ?.sort((a: any, b: any) => {
            const sectionA = a.Section?.DisplayOrder ?? -1
            const sectionB = b.Section?.DisplayOrder ?? -1

            if(sectionA > sectionB) {
                return 1
            } else if (sectionA < sectionB) {
                return -1
            }
            return a.DisplayOrder > b.DisplayOrder ? 1 : -1
        })
        ?.map((x: any, i: any) => (
            {   ...x,
                'SectionHeading': x?.SectionHeading ?? (x.Section?.Heading || null),
                DisplayOrder: i,
                LineNumber: i + 1, // remember to map line number as it is used for ordering on the section table

            })) || [])


    const [manageFormDefItem, setManageFormDefItem] = useState<any>(null)
    const [createNewFormDefItem, setCreateNewFromDefItem] = useState(false)


    const handleItemAction = (action: string, item: any) => {
        if (action === "delete") {
            // Extract ID of the item to be deleted
            const { ID } = item;
            // Remove the item from the formDefinitionFields
            !submitting && setFormDefinitionFields((prevFields: any) => {
                const updatedFields = reorderSectionsToBottom(
                    prevFields.filter((field: any) => field.ID !== ID)
                    );

                // Synchronize with formDefinition after removing the item
                setFormDefinition({
                    ...formDefinition,
                    FormDefinitionFields: updatedFields,
                });

                submit(form.getTransformedValues(), undefined, updatedFields);

                return updatedFields;
            });
            // Notify the user
            /*showNotification({
                title: "Item Deleted",
                message: `The item with ID ${ID} has been successfully deleted.`,
                color: "green",
            });*/
        } else if (action === 'edit') {
            !submitting && setManageFormDefItem(item);
        } else {
            console.warn(`Action "${action}" is not supported.`);
            return;
        }

    };

    const [editingSection, setEditingSection] = useState<Section | null>(null)

    const handleSectionItemAction = (action: string, tableGroup: any) => {


        if (action === 'delete') {
            const { id } = tableGroup;
            // Confirm deletion
            const confirmDelete = true

            if (confirmDelete) {
                // Remove item from form definition fields
                setFormDefinitionFields((prevFields: any) => {
                    const filtered = reorderSectionsToBottom(prevFields.filter((field: any) => field.SectionID !== id))

                    setFormDefinition({
                        ...formDefinition,
                        FormDefinitionFields: filtered,
                    });

                    submit(form.getTransformedValues(), undefined, filtered);
                    return filtered;
                });


               /* showNotification({
                    title: 'Success',
                    message: 'Section item deleted successfully!',
                    color: 'green',
                });*/
            }
        } else if (action === 'edit') {
            const { id } = tableGroup;
            setEditingSection(formDefinitionFields.find((x: any) => x.SectionID === id)?.Section || null);
        } else {
            console.warn(`Action ${action} is not handled.`);
        }
    };

    const [confirmOptions, setConfirmOptions] = useState(Helper.initialiseConfirmOptions());
    const allowNavRef = useRef(false);
    const allowNavigation = () => {
        allowNavRef.current = true;
    };
    const checkConfirmNavigation = () => {
        return () => !isNew && !isPublished && !form.isDirty() && form.values.IsActive && !allowNavRef.current;
    };
    Helper.preventRouteChange(form.isDirty(), () => form.resetDirty(), setConfirmOptions, () => submit(form.getTransformedValues()));
    Helper.preventRouteChangeGeneric(checkConfirmNavigation(), allowNavigation,
        "Form is not published",
        "Are you sure you want to navigate away? A draft form will not be visible to users in its current version.",
        (x: any) => setConfirmOptions(x)
    );

    const [printing, setPrinting] = useState(false)
    const preview = async () => {
        setPrinting(true);
        await previewFormDefinition(formDefinition.ID, () => setPrinting(false));
    }

    const confirmFormDefinition = async () => {
        setConfirming(true);
        setConfirmOptions({
            ...Helper.initialiseConfirmOptions(),
            display: true,
            confirmButtonText: "Publish",
            heading: "Publish form?",
            text: "Publishing will make this form available to users for jobs and customers.",
            onCancel: () => setConfirming(false),
            onConfirm: () => {
                submit(form.getTransformedValues(), undefined, undefined, Enums.FormDefinitionStatus.Published)
            }
        })
    };

    // Shared with the top-level "Revert to published" menu action and FormDefinitionVersionHistory's
    // per-row revert control - both hit the same /FormDefinition/Revert endpoint and need to reconcile
    // local state (fields, form values, URL) against whichever row comes back as the new current draft.
    const handleReverted = async (revertedFormDefinition: any) => {
        const fieldsWithSectionData = revertedFormDefinition.FormDefinitionFields?.map((x: any) => ({'SectionHeading': x?.SectionHeading ?? (x.Section?.Heading || null), LineNumber: x.DisplayOrder + 1, ...x}))
        setFormDefinitionFields(reorderSectionsToBottom(fieldsWithSectionData))
        setFormDefinition(revertedFormDefinition);
        // Revert never changes MasterID, and the route change below (when it happens) is shallow and
        // doesn't remount FormDefinitionVersionHistory, so its cached formDefinitionVersions query
        // wouldn't otherwise pick up the new post-revert state - same reasoning as the Publish path above.
        queryClient.invalidateQueries({queryKey: ['formDefinitionVersions', revertedFormDefinition.MasterID]});
        form.setValues({
            ...revertedFormDefinition,
            FormRule: formRuleOption(revertedFormDefinition.FormRule),
            formExpires: !!revertedFormDefinition.ExpireTimespan && revertedFormDefinition.ExpireTimespan > 0,
            FormExpiresTimespan: revertedFormDefinition.ExpireTimespan,
            MultiUse: revertedFormDefinition.MultiUse ?? !((revertedFormDefinition as any).SingleUse ?? false),
        })
        form.resetDirty()
        form.resetTouched()
        if (formDefinition.ID !== revertedFormDefinition.ID) {
            const restoreRef = allowNavRef.current;
            allowNavigation();
            await Helper.waitABit()
            await router.replace(`/settings/form/${revertedFormDefinition.ID}`, undefined, { shallow: true });
            await Helper.waitABit()
            allowNavRef.current = restoreRef;
        }
    };

    const [reverting, setReverting] = useState(false);
    const revertToPublished = () => {
        setConfirmOptions({
            ...Helper.initialiseConfirmOptions(),
            display: true,
            heading: 'Revert to published version?',
            text: 'This will discard the unpublished changes in this draft and make the currently published version active again. This cannot be undone.',
            confirmButtonText: 'Revert to published version',
            onConfirm: async () => {
                setReverting(true);
                try {
                    const result = await Fetch.post({
                        url: '/FormDefinition/Revert',
                        params: {FormDefinitionID: formDefinition.ID}
                    });
                    if (result?.ID) {
                        showNotification({
                            message: 'Reverted to the published version',
                            color: 'scBlue',
                            autoClose: 2000
                        });
                        await handleReverted(result);
                    } else {
                        showNotification({
                            message: result?.serverMessage || result?.message || 'Failed to revert',
                            color: 'yellow.7',
                        });
                    }
                } finally {
                    setReverting(false);
                }
            }
        });
    };

    const onTableInputChange = (name: string, item: any, value: any) => {
        // Update the individual field in formDefinitionFields
        if (name === 'Description') {
            setFormDefinitionFields((prevFields: any) => {
                const updatedFields = reorderSectionsToBottom(
                    prevFields.map((field: any, index: any) =>
                    (field.ID === item.ID) ? {...field, ...item, Description: value} : field
                ))

                console.log('updated fields', updatedFields, formDefinition)

                // Reflect the change in the parent form definition too
                setFormDefinition({
                    ...formDefinition,
                    FormDefinitionFields: updatedFields,
                });

                submit(form.getTransformedValues(), undefined, updatedFields);

                return updatedFields;
            });
        }
        // Optionally trigger form submission or validation
    };

    // Name/Description/Job types/Single-use fields — shared, always-visible content used by both the
    // "create new form" layout and the "edit existing form" (tabbed) layout. Extracted so it can be
    // referenced from both branches below without duplicating the underlying JSX/logic.
    const formFieldsSection = (
        <>
                <Flex wrap={'wrap'} gap={'sm'} direction={{base: 'column', md: 'row'}}>
                    <Box maw={500} style={{flexGrow: 1}}>
                        <ScTextControl
                            label="Name"
                            placeholder="Name of the form"
                            withAsterisk
                            maxLength={NAME_MAX}
                            disabled={structureLocked}
                            description={structureLocked ? 'This form\'s structure is locked, so its name cannot be changed.' : undefined}
                            {...form.getInputProps('Name')}
                        />
                    </Box>

                    <Box maw={500} style={{flexGrow: 1}}>
                        <ScTextAreaControl
                            autosize
                            maxRows={20}
                            label="Description"
                            placeholder="Description"
                            maxLength={DESCRIPTION_MAX}
                            {...form.getInputProps('Description')}
                        />
                        <Text
                            size={'xs'}
                            c={(form.values?.Description?.length || 0) > DESCRIPTION_MAX ? 'yellow.7' : 'dimmed'}
                        >
                            {(form.values?.Description?.length || 0)} / {DESCRIPTION_MAX}
                            {(form.values?.Description?.length || 0) > DESCRIPTION_MAX ? ' – please shorten' : ''}
                        </Text>
                    </Box>
                </Flex>
                <Flex wrap={'wrap'} gap={'sm'}>
                    <Box maw={500} style={{flexGrow: 1}}>
                        <Flex align={'center'} gap={'xs'} mt={'sm'}>
                            <Text fw={500} size={'sm'}>Link form to <Text span c={'var(--mantine-color-error)'}>*</Text></Text>
                            <Tooltip label={"Link the form to a job or customer"} color={'scBlue'}>
                                <ActionIcon variant={'transparent'} color={'scBlue'} radius={'xl'}>
                                    <IconInfoCircle size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Flex>
                        <SCDropdownList
                            mt={0}
                            name="FormRule"
                            options={Enums.getEnumItemsVD(Enums.FormRule).filter((x: any) => x.value !== 0)}
                            {...form.getInputProps('FormRule')}
                            onChange={(x: any) => form.setFieldValue('FormRule', x)}
                            required={true}
                            dataItemKey={"value"}
                            textField={"description"}
                            disabled={!isNew}
                            mantineComboboxProps={{ withinPortal: true }}
                        />

                        {
                            (!isNew && formDefinition.NonExpiring) || !form.values.FormRule || ((form.values.FormRule as any).value !== Enums.FormRule.Customer) ? "" : <>
                                <Box maw={500} style={{alignSelf: 'end'}} mb={0}>
                                    <SCSwitch label="Form Expires"
                                              checked={form.values.formExpires}
                                              onToggle={(checked: any) => {
                                                  form.setFieldValue('formExpires', checked);
                                                  if (checked) {
                                                      form.setFieldValue('FormExpiresTimespan', 1 * 60 * 24 * 7); // 1 week default
                                                  } else {
                                                      form.setFieldValue('FormExpiresTimespan', 0);
                                                  }
                                              }}
                                    />
                                </Box>
                                {
                                    form.values.formExpires &&
                                    <Flex wrap={'wrap'} gap={0} maw={500}>
                                        <MinuteSelector
                                            {...{} as any}
                                            label="Form Expiration"
                                            name="ExpireTimespan"
                                            required={true}
                                            defaultUnit="Month"
                                            readOnly={formDefinition.StructureLocked}
                                            {...form.getInputProps('FormExpiresTimespan')}
                                        />
                                    </Flex>
                                }
                            </>
                        }

                    </Box>

                    <Box maw={500} style={{flexGrow: 1}}>
                        <Flex align={'center'} gap={'xs'} mt={'sm'}>
                            <Text fw={500} size={'sm'}>Job types</Text>
                            <Tooltip color={'scBlue'} label={"Specify which job types this form applies to"}>
                                <ActionIcon variant={'transparent'} color={'scBlue'} radius={'xl'}>
                                    <IconInfoCircle size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Flex>
                        <SCMultiSelect availableOptions={jobTypes} selectedOptions={form.values.JobTypes}
                                       mt={0 as any}
                                       textField={'Name'} dataItemKey={'ID'}
                                       {...form.getInputProps('JobTypes')}
                        />

                        {/*{!isNew && !formDefinition.StructureLocked &&
                            <SCSwitch
                                label="Active"
                                checked={form.values.IsActive}
                                onToggle={(p) => form.setFieldValue('IsActive', p)}
                            />
                        }*/}
                    </Box>

                </Flex>


                {/* Single-use checkbox (create and edit) */}
                <Box mt={'xl'} mb={'md'}>
                    <Text c={'dark.7'} size={'sm'}>
                        Re-use forms up to 20 times per job, or restrict them to single-use only.
                    </Text>
                    <Flex align={'center'} gap={'xs'} mt={'xs'}>
                        <Checkbox
                            label="Single use only"
                            checked={!form.values.MultiUse}
                            disabled={structureLocked || (!isNew && !!form.values.MultiUse)}
                            onChange={(e: any) => {
                                const singleUse = e.currentTarget.checked;
                                if (isNew) {
                                    // On create, freely toggle between single and multi use
                                    form.setFieldValue('MultiUse', !singleUse);
                                } else {
                                    // On edit
                                    if (!singleUse) {
                                        // Switching from single-use to multi-use requires confirmation and is permanent
                                        setConfirmOptions({
                                            ...Helper.initialiseConfirmOptions(),
                                            display: true,
                                            heading: 'Change to multi-use?',
                                            text: 'Multi-use forms cannot be changed back to single-use once saved. Are you sure you want to proceed?',
                                            confirmButtonText: 'Make multi-use permanently',
                                            cancelButtonText: 'Cancel',
                                            onCancel: () => {
                                                // Revert visual state to single-use
                                                form.setFieldValue('MultiUse', false);
                                                setConfirming(false);
                                            },
                                            onConfirm: () => {
                                                // Do not auto-save. Mark form dirty and let user Save/Publish.
                                                form.setFieldValue('MultiUse', true);
                                                setFormDefinition({...formDefinition, FormDefinitionStatus: Enums.FormDefinitionStatus.Draft});
                                                /*showNotification({
                                                    message: 'Changed to multi-use. Publish to apply.',
                                                    color: 'scBlue',
                                                    autoClose: 2000
                                                });*/
                                            }
                                        });
                                    }
                                    // If trying to set singleUse=true on edit while already multi-use, control is disabled above
                                }
                            }}
                        />
                        <Tooltip
                            color={'scBlue'}
                            label={
                                isNew
                                    ? <>
                                        <Text size={'sm'} fw={600}>Leaving this unchecked makes the form multi-use</Text>
                                        <Text size={'sm'}>Multi-use forms cannot be changed back to single-use once saved</Text>
                                    </>
                                    : structureLocked
                                        ? 'This form\'s structure is locked, so its single-use setting cannot be changed'
                                        : (!form.values.MultiUse
                                            ? 'Multi-use forms cannot be changed back to single-use once saved'
                                            : 'This form is multi-use and cannot be changed to single use')
                            }
                        >
                            <ActionIcon variant={'transparent'} color={'scBlue'} radius={'xl'}>
                                <IconInfoCircle size={16} />
                            </ActionIcon>
                        </Tooltip>
                    </Flex>
                </Box>
        </>
    );

    return (
        <>
            <ConfirmAction options={confirmOptions} setOptions={setConfirmOptions} />

            <form onSubmit={form.onSubmit(submit)}>

                <LoadingOverlay
                    visible={formDefinitionQuery.isFetching} zIndex={1000} overlayProps={{ radius: "sm", blur: 2 }}
                />

                {isNew && <>
                    <Flex align={'center'} justify={'space-between'} mt={'sm'} wrap={'wrap-reverse'}>
                        <Flex align={'center'} gap={'sm'} justify={'space-between'} wrap={'nowrap'} style={{flexGrow: 1}}>
                            <Title order={3} c={'scBlue'}>
                                Create Form
                            </Title>
                        </Flex>
                    </Flex>

                    <Text c={'dark.7'} size={'sm'} mt={'xs'} mb={'sm'}>
                        <div>
                            Create custom forms to collect customer information or capture on-site job details. <br/>
                            Build as many as you need — from onsite checklists to inspection guides — and tailor them
                            to different job types.
                        </div>

                        <br/>
                    </Text>

                    {formFieldsSection}

                    <Flex justify={'end'} mt={'xl'} gap={5}>
                        <Link href={'/settings/form/list'}>
                            <Button variant={'outline'} color={'gray.7'} onClick={() => Helper.nextLinkClicked('/settings/form/list')} disabled={submitting}  >
                                Cancel
                            </Button>
                        </Link>
                        <Button variant={'scBlue'} type={'submit'} loading={submitting}>
                            Create
                        </Button>
                    </Flex>
                </>}

                {!isNew && (

                    <Box mt="md">
                        {/* Sticky header cluster: title/meta and actions share one row (actions wrap below
                            the title on narrow viewports), with the tab strip beneath. Stays pinned while the
                            panel content below scrolls. */}
                        <Box
                            style={{
                                position: 'sticky',
                                // Sit just below the app's fixed AppShell.Header rather than at the literal
                                // viewport top — otherwise this slides underneath it (it's above us, z-index
                                // 103). --app-shell-header-height is set by Mantine's AppShell (Layout.tsx) and
                                // already tracks the message-bar/banner state, so this stays correct whether or
                                // not a banner is showing, with no need to duplicate that logic here.
                                top: 'var(--app-shell-header-height, 0px)',
                                zIndex: 10,
                                backgroundColor: 'var(--mantine-color-body)',
                                // The Flex below's own mt="sm" collapses through this Box (a plain block with no
                                // padding/border to stop it), so once stuck it sits flush against the navbar with
                                // no visible gap. Padding on the Box itself can't collapse away, so it's the actual
                                // source of breathing room here — the child's margin was doing nothing.
                                paddingTop: '0.75rem',
                            }}
                        >
                            <Flex align={'flex-start'} justify={'space-between'} gap={'sm'} wrap={'wrap'} mb={'sm'}>
                                <Box style={{flexGrow: 1, minWidth: 200}}>
                                    <Flex align={'center'} gap={'sm'} wrap={'wrap'}>
                                        <Title order={3} c={'scBlue'}>
                                            {`Editing ${form.values.Name} `}
                                        </Title>
                                        {/* Archived status takes precedence in this single badge - showing "Archived" alongside
                                            "Published"/"Draft" at the same time was confusing, and an archived form's
                                            draft/publish status isn't actionable until it's restored anyway. Colors and
                                            the "Published" label match the Status column on the forms list (SettingsFormList /
                                            Enums.FormDefinitionStatusColor: Draft=Orange, Published=Blue, Discarded=Red). */}
                                        <Badge color={!form.values.IsActive ? 'gray' : isPublished ? 'scBlue' : isDiscarded ? 'red' : 'orange'} variant={'light'} radius={'sm'}>
                                            {!form.values.IsActive ? 'Archived' : isPublished ? 'Published' : isDiscarded ? 'Discarded' : 'Draft'}
                                        </Badge>
                                    </Flex>
                                    <Text c={'dark.6'} size={'sm'} mt={4}>
                                        {`Last Updated: ${moment(formDefinition.ModifiedDate || formDefinition.CreatedDate).format('DD MMM, h:mmA')} • Version ${formDefinition.Version ?? 0}`}
                                    </Text>
                                </Box>

                                <Flex align={'center'} gap={8} wrap={'wrap'}>
                                    {/* Save/Publish render on both tabs (not just Details) for consistency -
                                        the underlying <form> wraps both Tabs.Panels, so submitting works either way. */}
                                    <Button color={'scBlue'} variant={'outline'} type={'submit'} loading={submitting} disabled={isDiscarded || accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}>
                                        {submitting && !confirming ? 'Saving' : 'Save'}
                                    </Button>
                                    {/* Wrapped in a span so the Tooltip still receives hover events while the
                                        Button itself is natively disabled (a disabled <button> doesn't fire them). */}
                                    <Tooltip label={'Restore this form to publish it'} disabled={form.values.IsActive} color={'scBlue'}>
                                        <Box component={'span'} style={{display: 'inline-block'}}>
                                            <Button
                                                color={'scBlue'}
                                                variant={'filled'}
                                                type={'button'}
                                                onClick={() => !submitting && confirmFormDefinition()}
                                                loading={confirming}
                                                disabled={
                                                    !form.values.IsActive || formDefinitionFields.length === 0 || isPublished || isDiscarded || submitting || accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess
                                                }
                                            >
                                                {submitting && confirming ? 'Publishing' : 'Publish'}
                                            </Button>
                                        </Box>
                                    </Tooltip>

                                    <Tooltip label={'Preview form'} color={'scBlue'}>
                                        <ActionIcon
                                            aria-label={'Preview form'}
                                            loading={printing}
                                            onClick={() => preview()}
                                            disabled={submitting}
                                            variant={'outline'}
                                            color={'scBlue'}
                                            size={'lg'}
                                        >
                                            <IconEye size={18} />
                                        </ActionIcon>
                                    </Tooltip>

                                    <Tooltip label={'More actions'} color={'scBlue'}>
                                        <Menu shadow={'md'} width={220} position={'bottom-end'} withinPortal>
                                            <Menu.Target>
                                                <ActionIcon aria-label={'More actions'} variant={'outline'} color={'scBlue'} size={'lg'}>
                                                    <IconDotsVertical size={18} />
                                                </ActionIcon>
                                            </Menu.Target>
                                            <Menu.Dropdown>
                                                <Menu.Item
                                                    leftSection={form.values.IsActive ? <IconArchive style={{color: 'var(--mantine-color-yellow-8)'}} size={16} /> : <IconArchiveOff size={16} />}
                                                    color={form.values.IsActive ? 'yellow.8' : 'scBlue'}
                                                    c={form.values.IsActive ? 'gray.7' : undefined}
                                                    disabled={isDiscarded || submitting || accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess || (structureLocked && form.values.IsActive)}
                                                    onClick={() => setConfirmOptions({
                                                        ...Helper.initialiseConfirmOptions(),
                                                        display: true,
                                                        heading: form.values.IsActive ? 'Archive this form?' : 'Restore this form?',
                                                        text: form.values.IsActive
                                                            ? `"${form.values.Name}" won't be available to add to new jobs. It will still be available on jobs that already had the form, and you can restore it at any time.`
                                                            : `Restoring makes "${form.values.Name}" available to add to new jobs again.`,
                                                        confirmButtonText: form.values.IsActive ? 'Archive' : 'Restore',
                                                        onConfirm: () => submit({...form.getTransformedValues(), IsActive: !form.values.IsActive}, undefined, undefined, formDefinition.FormDefinitionStatus),
                                                    })}
                                                >
                                                    {form.values.IsActive ? 'Archive' : 'Restore'}
                                                </Menu.Item>
                                                {/* Revert isn't relevant while archived - restore the form first. Removed
                                                    outright rather than disabled, since a Menu.Item's native disabled
                                                    state can't reliably carry an explanatory tooltip. */}
                                                {form.values.IsActive && <>
                                                    <Menu.Divider />
                                                    <Menu.Item
                                                        leftSection={<IconArrowBackUp style={{color: 'var(--mantine-color-red-5)'}} size={16} />}
                                                        color={'red'}
                                                        c={'gray.7'}
                                                        disabled={isPublished || isDiscarded || submitting || reverting || accessStatus === Enums.AccessStatus.LockedWithAccess || accessStatus === Enums.AccessStatus.LockedWithOutAccess}
                                                        onClick={revertToPublished}
                                                    >
                                                        {reverting ? 'Reverting…' : 'Revert to published'}
                                                    </Menu.Item>
                                                </>}
                                            </Menu.Dropdown>
                                        </Menu>
                                    </Tooltip>
                                </Flex>
                            </Flex>

                            <PageTabs
                                selectedTab={activeTab}
                                setSelectedTab={setActiveTab}
                                tabs={pageTabs}
                                tabsProps={{
                                    styles: {
                                        root: {
                                            borderBottom: '1px solid var(--mantine-color-gray-2)'
                                        }
                                    }
                                }}
                            />
                        </Box>

                    {activeTab === 'Details' &&
                    <div style={{paddingTop: 'var(--mantine-spacing-md)'}}>

                        {formFieldsSection}

                        {
                            (manageFormDefItem || createNewFormDefItem) &&
                            <ManageFormDefinitionField
                                savingForm={submitting}
                                formDefinitionField={manageFormDefItem}
                                saveFormDefinitionField={(fieldToSave: any, andClose?: boolean) => {
                                    if (fieldToSave) {
                                        if (createNewFormDefItem) {
                                            setFormDefinitionFields((p: any) => {
                                                const updatedFormDefinitionFields = reorderSectionsToBottom(p.concat({
                                                    ...fieldToSave,
                                                    SectionHeading: fieldToSave.Section?.Heading,
                                                    // DisplayOrder: fieldToSave.DisplayOrder || p.length,
                                                    DisplayOrder: p.length,
                                                    LineNumber: p.length + 1,
                                                    // ID: 'new' + Date.now(),
                                                }));
                                                setFormDefinition({
                                                    ...formDefinition,
                                                    FormDefinitionFields: updatedFormDefinitionFields
                                                });
                                                form.validate() && submit(form.getTransformedValues(), undefined, updatedFormDefinitionFields)
                                                return updatedFormDefinitionFields;
                                            });
                                        } else {
                                            setFormDefinitionFields((p: any) => {
                                                const sectionIdChanged = fieldToSave.SectionID !== p.find((x: any) => x.ID === fieldToSave.ID)
                                                let sectionItemLastIndex = -1
                                                sectionIdChanged && p.forEach((x: any) => {
                                                    if (x.SectionID === fieldToSave.SectionID) {
                                                        sectionItemLastIndex = x.DisplayOrder + 1
                                                    }
                                                })
                                                const updatedFormDefinitionFields = reorderSectionsToBottom(p.map((x: any) => x.ID === fieldToSave.ID ? ({
                                                    ...fieldToSave,
                                                    DisplayOrder: sectionIdChanged ? fieldToSave.SectionID ? sectionItemLastIndex : p.length + 1 : x.DisplayOrder,
                                                    LineNumber: sectionIdChanged ? fieldToSave.SectionID ? sectionItemLastIndex + 1 : p.length + 1 : x.LineNumber,
                                                    SectionHeading: fieldToSave.Section?.Heading,
                                                }) : x));
                                                setFormDefinition({
                                                    ...formDefinition,
                                                    FormDefinitionFields: updatedFormDefinitionFields
                                                });
                                                form.validate() && submit(form.getTransformedValues(), undefined, updatedFormDefinitionFields)
                                                return updatedFormDefinitionFields;
                                            });
                                        }
                                    }
                                    if(andClose !== false) {
                                        setCreateNewFromDefItem(false);
                                        setManageFormDefItem(null);
                                    }
                                }}
                                isNew={createNewFormDefItem}
                                formDefinition={formDefinition}
                                updateFormDefinition={(x: any) => console.log('update form definition', x, 'not implemented')}
                                structureLocked={formDefinition.StructureLocked}
                            />
                        }

                        {
                            editingSection && <>
                            <ManageSection
                                onSave={(saveItem: Section) => {
                                    if (saveItem) {
                                        setFormDefinitionFields((p: any) => reorderSectionsToBottom(p.map((x: any) => x.SectionID === saveItem.ID ? ({...x, Section: saveItem, SectionHeading: saveItem.Heading}) : x)));
                                    }
                                    setEditingSection(null);
                                    setEditingSection(null)
                                    formDefinitionQuery.refetch()
                                }}
                                module={Enums.Module.FormDefinition}
                                dontSubmit={!formDefinition || !formDefinition.ID}
                                itemID={formDefinition.ID ?? null}
                                sectionID={editingSection.ID}
                                newSection={false}
                                displayOrder={editingSection.DisplayOrder || formDefinition?.Sections?.length || 0}
                            />
                            {/*<ManageSection
                                onSave={console.log}
                                module={Enums.Module.FormDefinition}
                                dontSubmit={!formDefinition || !formDefinition.ID}
                                itemID={formDefinition && formDefinition.ID ? formDefinition.ID : null}
                                sectionID={editingSection ? editingSection.ID : null}
                                newSection={editingSection ? editingSection : null}
                                displayOrder={editingSection ? editingSection.DisplayOrder : formDefinition && formDefinition.Sections ? formDefinition.Sections.length : 0}
                            />*/}
                        </>
                        }

                        <Box maw={850}>
                            <SectionTable
                                sectionHeaderDisplayValueFunction={(x: any) => {
                                    const section = formDefinition.Sections.find((y: any) => y.ID === x.id);
                                    return <Flex align={'center'} justify={'space-between'} gap={5}>{section?.Heading} {section?.Repeatable && <Text size={'sm'} fs={'italic'}>( Repeatable )</Text>}</Flex>
                                }}
                                savingItem={submitting}
                                allowSections={false}
                                stylingProps={{
                                    compact: true,
                                    darkerText: true
                                }}
                                data={formDefinitionFields}
                                height={'100%'}
                                mapping={
                                // placeholder header when no fields are added
                                formDefinitionFields.length === 0 ? [
                                    {
                                        key: 'Description',
                                        type: 'textArea',
                                        label: 'Add fields to your form'
                                    },
                                    {
                                        key: 'spacer1',
                                        type: 'textArea',
                                        label: <>&nbsp;</>,
                                    },
                                    {
                                        key: 'spacer2',
                                        type: 'textArea',
                                        label: <>&nbsp;</>,
                                    },
                                    {
                                        key: 'spacer3',
                                        type: 'textArea',
                                        label: <>&nbsp;</>,
                                    },
                                ] :
                                [
                                    /*{
                                        key: 'InventoryCode',
                                        label: '',
                                        valueFunction: (item: any) => <Flex align={'center'}><Text>Edit</Text></Flex>,
                                        linkAction: 'edit'
                                    },*/
                                    {
                                        key: 'Description',
                                        type: 'textArea',
                                        label: 'Description',
                                        placeholderFunction: () => 'Enter Field Description *',
                                        maxLength: 42,
                                    },
                                    {
                                        key: 'DataType',
                                        // type: 'status',
                                        // columnWidth: 50,
                                        // type: 'selectInput',
                                        label: 'Type',
                                        valueFunction: (x: any) => Enums.FormDefinitionFieldTypes[x.DataType],
                                        linkAction: 'edit'
                                        // selectOptions: Enums.getEnumItemsVD(Enums.FormDefinitionFieldTypes, true, true).map(x => ({label: x.description, ...x}))
                                    }
                                ]}
                                onInputChange={onTableInputChange}
                                onDataUpdate={(newItems: any) => {

                                    const updatedFields = reorderSectionsToBottom(newItems.map((x: any, i: any) => (
                                        {
                                            ...x,
                                            DisplayOrder: (x.LineNumber - 1) || i,
                                            Section: x.SectionID ?
                                                {
                                                    ...formDefinitionFields.find((y: any) => y.SectionID === x.SectionID)?.Section,
                                                    Heading: x.SectionHeading,
                                                } : null
                                        }
                                    )));
                                    setFormDefinitionFields(updatedFields);
                                    submit(form.getTransformedValues(), undefined, updatedFields);
                                }}
                                onSectionItem={handleSectionItemAction}
                                onAction={handleItemAction}
                                sectionControls={
                                    [
                                        ...(!formDefinition.StructureLocked && !isDiscarded ?
                                            [
                                                {
                                                    name: 'edit',
                                                    icon: IconEdit,
                                                    color: 'scBlue',
                                                    label: 'Edit Section'
                                                },
                                                {
                                                    name: 'ungroup'
                                                },
                                                {
                                                    name: 'delete',
                                                    icon: IconTrash,
                                                    color: 'yellow.7',
                                                    label: 'Delete Section',
                                                }
                                            ] : [])
                                    ]}
                                showControlsOnHover={false}
                                controls={[
                                    ...(!formDefinition.StructureLocked && !isDiscarded ? [
                                        {
                                            name: 'edit',
                                            icon: <IconEdit/>,
                                            label: 'Edit',
                                            buttonProps: {disabled: submitting}
                                        },
                                        {
                                            name: 'delete',
                                            icon: <IconTrash/>,
                                            type: 'warning' as any,
                                            label: 'Remove',
                                            buttonProps: {ml: 20, disabled: submitting}
                                        },
                                    ] : [])
                                ]}
                                sectionTitleKey={'SectionHeading'}
                                sectionIdKey={'SectionID'}
                                module={Enums.Module.FormDefinition}
                                itemId={''}
                                canEdit={!formDefinition.StructureLocked && !isDiscarded && !submitting}
                                addButton={{
                                    customComponent: <Flex w={'100%'} gap={'md'}>
                                        {!formDefinition.StructureLocked && !isDiscarded && accessStatus !== Enums.AccessStatus.LockedWithAccess && accessStatus !== Enums.AccessStatus.LockedWithOutAccess && <>
                                            <Button c={'scBlue'}
                                                    onClick={
                                                        () => setCreateNewFromDefItem(true)
                                                    }
                                                    size={'compact-sm'}
                                                    variant={'transparent'}
                                                    type={'button'}
                                            >
                                                <IconPlus size={16}/>
                                                <Anchor size={'sm'}>
                                                    Add Field
                                                </Anchor>
                                            </Button>
                                        </>}
                                    </Flex>
                                }}
                            />
                        </Box>


                        {/*Legacy
                    <FormDefinitionFields key={1}
                                          formDefinitionFields={formDefinition.FormDefinitionFields}
                                          updateFormDefinitionFields={console.log}
                                          inputErrors={[]} accessStatus={accessStatus}
                                          formDefinition={formDefinition} updateFormDefinition={setFormDefinition}
                                          structureLocked={formDefinition.StructureLocked}
                    />*/}
                    </div>
                    }

                    {activeTab === 'Version history' &&
                        <Box pt="md">
                            <FormDefinitionVersionHistory
                                masterID={formDefinition.MasterID}
                                onPrintStateChange={setPrinting}
                                onReverted={handleReverted}
                            />
                        </Box>
                    }
                    </Box>

                )}

            </form>

        </>
    );
};

export default ManageFormDefinition;
